import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Text,
} from "@chakra-ui/react";
import { juryOnlyAdvises } from "./juryRanking";
import { designContestPrizesOf, eventIsDesignContest, tenderAwardCriteriaOf } from "../../sourcing-events/buyer/wizard/utils/designContest";
import { useTranslation } from "react-i18next";
import {
  AwardLetterStatusDto,
  AwardLetterTypeDto,
  type SourcingEventDto,
  type WithdrawnAwardLettersRoundDto,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useRunsPublicProcurements } from "../../../common/organization/useRunsPublicProcurements";
import { useProcurementTerms } from "../../../common/terminology/useProcurementTerms";
import { awardNotificationCriteria } from "../../sourcing-events/portal/view-single/awardNotificationCriteria";
import { AwardLetterPreview } from "./AwardLetterPreview";
import { winnerNamesText, withdrawnStandstill } from "./awardLetterTemplates";
import { invitesItsSuppliers } from "../changes/changeScope";

export const WithdrawnAwardLetters = ({
  event,
  round,
  namesWinnerToOtherBidders,
  standstillDays,
}: {
  event: SourcingEventDto;
  round: WithdrawnAwardLettersRoundDto | undefined;
  namesWinnerToOtherBidders: boolean;
  standstillDays?: number | null;
}) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const runsPublicProcurements = useRunsPublicProcurements();
  const senderName = useLoggedInWithOrgContextState().selectedOrg.name;
  const sent = (round?.letters ?? []).filter((letter) => letter.status === AwardLetterStatusDto.Sent);
  if (!round || sent.length === 0) return null;

  const winnerNames = round.letters
    .filter((letter) => letter.type === AwardLetterTypeDto.Winner)
    .map((letter) => letter.supplierName)
    .filter((name): name is string => !!name);
  const winnerName = namesWinnerToOtherBidders ? winnerNamesText(winnerNames) : "";

  return (
    <Accordion allowToggle mt="2">
      <AccordionItem border="1px solid" borderColor="smBorder" rounded="md">
        <AccordionButton px="3" py="2">
          <Text flex="1" textAlign="left" fontSize="sm" fontWeight="semibold" color="smText">
            {t("Award letters sent for this decision ({{count}})", { count: sent.length })}
          </Text>
          <AccordionIcon color="smMuted" />
        </AccordionButton>
        <AccordionPanel px="3" pb="3">
          <Accordion allowToggle>
            {sent.map((letter) => {
              const evaluation = round.evaluations?.[letter.bidId];
              return (
                <AccordionItem key={letter.id} borderColor="smBorder">
                  <AccordionButton px="2" py="2">
                    <Box flex="1" textAlign="left">
                      <Text fontSize="sm" color="smText">
                        {letter.supplierName || t("Unknown")}
                      </Text>
                      <Text fontSize="xs" color="smMuted">
                        {letter.type === AwardLetterTypeDto.Winner ? t("Winner letter") : t("Rejection letter")}
                      </Text>
                    </Box>
                    <AccordionIcon color="smMuted" />
                  </AccordionButton>
                  <AccordionPanel px="2" pb="3">
                    <AwardLetterPreview
                      type={letter.type}
                      forRejectedTender={letter.forRejectedTender}
                      chosenTenderAdvantages={letter.chosenTenderAdvantages}
                      supplierName={letter.supplierName || ""}
                      winnerName={winnerName}
                      eventTitle={event.eventData.title}
                      senderName={senderName}
                      scoreHeading={terms.scorePerAwardCriterion}
                      criterionJustifications={letter.content.criterionJustifications ?? {}}
                      freeText={letter.content.freeText}
                      rankingDepartureExplanation={letter.content.rankingDepartureExplanation}
                      criteria={awardNotificationCriteria(
                        evaluation?.criteria ?? [],
                        tenderAwardCriteriaOf(event)?.bestPriceQualityRatio?.criteria ?? [],
                        { criterion: t("Criterion") || "", price: t("Price") || "" },
                      )}
                      evaluation={evaluation}
                      sentAt={letter.sentAt}
                      sentClauses={letter.sentClauses}
                      clauseContext={{
                        eventRunsPrivateFlow: invitesItsSuppliers(event),
            outsideProcurementAct: event.outsideProcurementAct ?? false,
                        buyerRunsPublicProcurements: runsPublicProcurements,
                        regulation: event.eventContext.type === "Public" ? event.eventContext.regulation ?? null : null,
            designContest: eventIsDesignContest(event),
            juryOnlyAdvises: juryOnlyAdvises(event),
            designContestPrizes: designContestPrizesOf(event),
                        standstill: withdrawnStandstill(
                          event.buyerSetsStandstill ?? false,
                          event.choosesVoluntaryStandstill ?? false,
                          standstillDays,
                        ),
                      }}
                    />
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
